var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
import { jsx } from "react/jsx-runtime";
import { LayoutSections, PRODUCT_ID, ProductSelectionContext, GridLayout, useGetAdProductsForAdPostingQuery } from "./styles/ProductSelection/components/layouts/GridLayout/GridLayout.mjs";
import { getDisplayPrices, TrackingContext } from "./styles/ProductSelection/components/elements/Expectations/RelevantApplications/RelevantApplications.mjs";
import { ContentBlock, Loader } from "braid-design-system";
import { useLanguage } from "@vocab/react";
import { useApolloClient } from "@apollo/client";
import { ShoppingCartContext } from "@seek/shopping-cart-provider";
import { useContext, useCallback, useEffect } from "react";
const LayoutFormats = ["grid"];
const DEFAULT_LAYOUT_FORMAT = "grid";
const isLayoutFormat = (format) => LayoutFormats.some((layoutFormat) => layoutFormat === format);
const mapLayoutFormat = (format) => isLayoutFormat(format) ? format : DEFAULT_LAYOUT_FORMAT;
const isLayoutSection = (section) => LayoutSections.includes(section);
const mapSectionOrder = (sections) => sections.filter(isLayoutSection);
const mapLayoutDisplay = (displayLayout) => ({
  format: mapLayoutFormat(displayLayout.format),
  sortProductIds: displayLayout.sortProductIds,
  sectionOrder: mapSectionOrder(displayLayout.sectionOrder),
  productCap: {
    products: displayLayout.productCap.products ?? void 0,
    upgrades: void 0
    // @TODO: Decide if want to include this
  },
  hide: []
  // @TODO: Decide if want to include this
});
const mapAddOns = (addOns) => addOns ? addOns.map(mapProduct) : [];
const BannerTypes = ["new", "featured", "current", "recommended"];
const BannerVariants = ["narrow", "wide"];
const isBannerType = (type) => BannerTypes.includes(type);
const mapBannerType = (type) => type && isBannerType(type) ? type : void 0;
const DEFAULT_VARIANT$4 = "wide";
const isBannerVariant = (variant) => BannerVariants.some((bannerVariant) => bannerVariant === variant);
const mapBannerVariant = (variant) => variant && isBannerVariant(variant) ? variant : DEFAULT_VARIANT$4;
const mapBanner = (banner) => {
  if (banner == null ? void 0 : banner.enabled) {
    const type = mapBannerType(banner.type);
    const variant = mapBannerVariant(banner.variant ?? void 0);
    return type ? { type, variant } : void 0;
  }
  return void 0;
};
const DEFAULT_VARIANT$3 = "select";
const CtaVariants = ["select", "add", "current", "upgrade"];
const isCtaVariant = (variant) => CtaVariants.some((ctaVariant) => ctaVariant === variant);
const mapCtaVariant = (variant) => variant && isCtaVariant(variant) ? variant : DEFAULT_VARIANT$3;
const mapCta = (cta) => {
  if (cta == null ? void 0 : cta.enabled) {
    const variant = mapCtaVariant(cta.variant ?? void 0);
    const url = cta.url ?? void 0;
    return { variant, url };
  }
  return void 0;
};
const CustomIds = [
  "ppa-content",
  "billable-units-inclusions",
  "billable-units-inclusions-2"
];
const isCustomId = (id) => CustomIds.some((customId) => customId === id);
const mapCustomContent = (custom) => isCustomId(custom.id) ? custom.id : void 0;
const mapCustom = (custom) => {
  const customContent = (custom == null ? void 0 : custom.enabled) ? custom.content.map(mapCustomContent).filter((id) => id !== void 0) : void 0;
  return customContent && customContent.length > 0 ? customContent : void 0;
};
const ExpectationVariants = [
  "featured",
  "deprioritised",
  "secondary"
];
const DEFAULT_VARIANT$2 = "featured";
const isExpectationVariant = (variant) => ExpectationVariants.some(
  (expectationVariant) => expectationVariant === variant
);
const mapExpectationVariant = (variant) => variant && isExpectationVariant(variant) ? variant : DEFAULT_VARIANT$2;
const mapExpectations = ({
  expectations,
  expectationForDraft,
  expectationForJob
}) => {
  const { applicationsCompleted: count, correlationId } = expectationForDraft ?? expectationForJob ?? {};
  const isEnabled = (expectations == null ? void 0 : expectations.enabled) !== false;
  return {
    count: typeof count === "number" && isEnabled ? count : void 0,
    unit: "applications",
    correlationId: typeof correlationId === "string" ? correlationId : void 0,
    variant: mapExpectationVariant((expectations == null ? void 0 : expectations.variant) ?? void 0)
  };
};
const ImageIds = [
  "branded-upgrade",
  "urgent-lite-upgrade",
  "urgent-hiring-immediate-start",
  "urgent-hiring-urgently-hiring"
];
const isImageId = (id) => ImageIds.some((imageId) => imageId === id);
const mapImageId$1 = (id) => isImageId(id) ? id : void 0;
const mapImage$1 = (image) => {
  if (image == null ? void 0 : image.enabled) {
    const imageId = mapImageId$1(image.id);
    return imageId ? { id: imageId, description: image.description ?? "" } : void 0;
  }
  return void 0;
};
const HelpTipImageIds = [
  "priority-listing-anz",
  "priority-listing-asia",
  "email"
];
const InclusionVariants = [
  "default",
  "deprioritised",
  "no-icon"
];
const DEFAULT_VARIANT$1 = "default";
const isHelpTipImageId = (id) => HelpTipImageIds.includes(id);
const mapImageId = (id) => id && isHelpTipImageId(id) ? id : void 0;
const mapImage = (image) => {
  const imageId = mapImageId(image.id);
  if (imageId) {
    return {
      id: imageId,
      description: image.description ?? void 0
    };
  }
  return void 0;
};
const mapHelpTip = (helpTip) => {
  const content = helpTip.content;
  const image = helpTip.image ? mapImage(helpTip.image) : void 0;
  if (content) {
    return { content, image };
  }
  return void 0;
};
const isInclusionVariant = (variant) => InclusionVariants.some((inclusionVariant) => inclusionVariant === variant);
const mapInclusionVariant = (variant) => variant && isInclusionVariant(variant) ? variant : DEFAULT_VARIANT$1;
const mapInclusions = (inclusions) => {
  if (inclusions == null ? void 0 : inclusions.enabled) {
    return {
      content: inclusions.content.map((item) => ({
        name: item.content,
        helpTip: item.helpTip ? mapHelpTip(item.helpTip) : void 0
      })),
      variant: mapInclusionVariant((inclusions == null ? void 0 : inclusions.variant) ?? void 0)
    };
  }
  return void 0;
};
const mapName = (displayMetadata, productName) => (displayMetadata == null ? void 0 : displayMetadata.name) ?? productName;
const PanelVariants = ["default", "featured", "featured-focus"];
const DEFAULT_VARIANT = "default";
const isPanelVariant = (variant) => PanelVariants.some((panelVariant) => panelVariant === variant);
const mapPanelVariant = (variant) => variant && isPanelVariant(variant) ? variant : DEFAULT_VARIANT;
const mapPanel = (panel) => ({
  variant: panel ? mapPanelVariant(panel.variant ?? void 0) : DEFAULT_VARIANT
});
const PriceVariants = [
  "billable-units-highlight",
  "billable-units-highlight-2"
];
const isPriceVariant = (variant) => PriceVariants.some((priceVariant) => priceVariant === variant);
const mapPriceVariant = (variant) => variant && isPriceVariant(variant) ? variant : void 0;
const mapPrice = (product, isAdditionalPrice = false) => {
  var _a, _b, _c, _d;
  if (((_b = (_a = product.display) == null ? void 0 : _a.price) == null ? void 0 : _b.enabled) === false) {
    return { type: "HiddenPrice" };
  }
  if ("pricing" in product && product.pricing && "unitPrice" in product.pricing && "cappedPrice" in product.pricing && product.pricing.unitPrice && "netPrice" in product.pricing.unitPrice && product.pricing.cappedPrice && "netPrice" in product.pricing.cappedPrice) {
    return {
      type: "CappedUnitPrice",
      // @TODO: read from product.pricing.metadata.unitType
      unitType: "application",
      maxBillableUnits: product.pricing.maxBillableUnits,
      unitPrice: {
        type: "FixedPrice",
        retailPrice: product.pricing.unitPrice.retailPrice,
        netPrice: product.pricing.unitPrice.netPrice,
        tax: product.pricing.unitPrice.tax ?? void 0,
        retailTax: product.pricing.unitPrice.retailTax ?? void 0,
        isAdditionalPrice
      },
      cappedPrice: {
        type: "FixedPrice",
        retailPrice: product.pricing.cappedPrice.retailPrice,
        netPrice: product.pricing.cappedPrice.netPrice,
        tax: product.pricing.cappedPrice.tax ?? void 0,
        retailTax: product.pricing.cappedPrice.retailTax ?? void 0,
        isAdditionalPrice
      },
      variant: mapPriceVariant(((_d = (_c = product.display) == null ? void 0 : _c.price) == null ? void 0 : _d.variant) ?? void 0)
    };
  }
  if (product.price && "netPrice" in product.price) {
    return {
      type: "FixedPrice",
      retailPrice: product.price.retailPrice,
      netPrice: product.price.netPrice,
      tax: product.price.tax ?? void 0,
      retailTax: product.price.retailTax ?? void 0,
      isAdditionalPrice
    };
  }
  return { type: "HiddenPrice" };
};
const mapTagline = (tagline) => {
  if (tagline) {
    const { enabled, ...rest } = tagline;
    return enabled ? rest : void 0;
  }
  return void 0;
};
const mapTitle = (title) => {
  if (title) {
    const { enabled, ...rest } = title;
    return enabled ? rest : void 0;
  }
  return void 0;
};
const mapType = (typename) => typename ?? "UnknownProduct";
const mapUpgradePaths = (upgradePaths, mapProduct2) => {
  if (!upgradePaths) {
    return [];
  }
  return upgradePaths.map(
    ({
      targetProduct2,
      upgradeProduct
    }) => {
      const targetProduct = targetProduct2;
      const isValidUpgrade = targetProduct.__typename === "AdProduct" || targetProduct.__typename === "AdProductUpgrade";
      if (!isValidUpgrade) {
        return void 0;
      }
      const isAdditionalPrice = upgradeProduct.id !== PRODUCT_ID.URGENT_LITE_UPGRADE;
      const data = mapProduct2(upgradeProduct);
      const { id } = targetProduct;
      const { id: upgradeProductId } = upgradeProduct;
      const name = mapName(targetProduct.displayMetadata, targetProduct.name);
      const price = mapPrice(upgradeProduct, isAdditionalPrice);
      return { ...data, id, name, price, upgradeProductId };
    }
  ).filter(
    (upgradePath) => upgradePath !== void 0
  );
};
const isAdProductAddOn = (product) => product.__typename === "AdProductAddOn";
const sortProducts = (products = [], sortedIds = []) => {
  const indexMap = new Map(sortedIds.map((id, index) => [id, index]));
  const maxIndex = Number.MAX_SAFE_INTEGER;
  const cloneProducts = Array.from(products);
  return cloneProducts.sort(
    (a, b) => (indexMap.get(a.id) ?? maxIndex) - (indexMap.get(b.id) ?? maxIndex)
  );
};
const mapProduct = (product) => {
  var _a, _b, _c, _d, _e, _f, _g, _h, _i;
  const baseProduct = {
    id: product.id,
    type: mapType(product.__typename),
    name: mapName(product.displayMetadata, product.name),
    panel: mapPanel((_a = product.display) == null ? void 0 : _a.panel),
    // TODO refactor this function
    price: mapPrice(product),
    title: mapTitle((_b = product.display) == null ? void 0 : _b.title),
    banner: mapBanner((_c = product.display) == null ? void 0 : _c.banner),
    tagline: mapTagline((_d = product.display) == null ? void 0 : _d.tagline),
    custom: mapCustom((_e = product.display) == null ? void 0 : _e.custom),
    inclusions: mapInclusions((_f = product.display) == null ? void 0 : _f.inclusions),
    cta: mapCta((_g = product.display) == null ? void 0 : _g.cta),
    image: mapImage$1((_h = product.display) == null ? void 0 : _h.image)
  };
  if (isAdProductAddOn(product)) {
    return {
      ...baseProduct,
      upgradePaths: [],
      addOns: []
    };
  }
  return {
    ...baseProduct,
    expectations: mapExpectations({
      expectations: (_i = product.display) == null ? void 0 : _i.expectations,
      expectationForDraft: product.expectationForDraft,
      expectationForJob: product.expectationForJob
    }),
    upgradePaths: mapUpgradePaths(product.upgradePaths, mapProduct),
    addOns: mapAddOns(product.addOns)
  };
};
const mapProducts = (products, layoutDisplayRules) => {
  var _a;
  const mappedProducts = products.map((product) => mapProduct(product));
  if (!layoutDisplayRules) return mappedProducts;
  const filteredProducts = mappedProducts.filter(
    (product) => {
      var _a2;
      return !((_a2 = layoutDisplayRules.hide) == null ? void 0 : _a2.includes(product.id));
    }
  );
  return (
    // 2. Sort the products
    sortProducts(filteredProducts, layoutDisplayRules.sortProductIds).slice(0, (_a = layoutDisplayRules.productCap) == null ? void 0 : _a.products).map((product) => {
      var _a2;
      const filteredUpgrades = product.upgradePaths.filter(
        (upgrade) => {
          var _a3;
          return !((_a3 = layoutDisplayRules.hide) == null ? void 0 : _a3.includes(upgrade.id));
        }
      );
      return {
        ...product,
        upgradePaths: filteredUpgrades.slice(
          0,
          (_a2 = layoutDisplayRules.productCap) == null ? void 0 : _a2.upgrades
        )
      };
    })
  );
};
const mapProductsForDisplayEvent = (products) => products == null ? void 0 : products.map((product) => {
  let productId = product.id;
  if (product.type === "AdProductUpgrade" && product.upgradeProductId) {
    productId = product.upgradeProductId;
  }
  const netPriceInput = product.price.type === "CappedUnitPrice" ? product.price.unitPrice : product.price;
  const { displayNetPrice: netPrice } = netPriceInput.type === "FixedPrice" ? getDisplayPrices(netPriceInput) : { displayNetPrice: void 0 };
  const { displayNetPrice: cappedPrice } = product.price.type === "CappedUnitPrice" && product.price.cappedPrice.type === "FixedPrice" ? getDisplayPrices(product.price.cappedPrice) : { displayNetPrice: void 0 };
  const expectation = product.expectations ? {
    ...typeof product.expectations.count === "number" ? { applicationsCompleted: product.expectations.count } : {},
    correlationId: product.expectations.correlationId
  } : void 0;
  return {
    productId,
    netPrice: netPrice == null ? void 0 : netPrice.price,
    ...cappedPrice ? { cappedPrice: cappedPrice.price } : void 0,
    ...expectation ? { expectation } : void 0
  };
});
const mapProductPricingForDisplayEvent = (products) => {
  const productPricing = products.map((product, index) => {
    var _a;
    return {
      position: index + 1,
      id: product.id,
      price: (product.price.type === "FixedPrice" ? product.price.netPrice.value : void 0) ?? -1,
      currency: product.price.type === "FixedPrice" ? product.price.netPrice.currency : void 0,
      priceHidden: typeof product.price === "undefined",
      ...((_a = product.expectations) == null ? void 0 : _a.unit) === "applications" && typeof product.expectations.count === "number" && {
        adProductsExpectedApps: product.expectations.count
      }
    };
  });
  return productPricing;
};
const prepareDisplayEvent = (data, meta) => {
  var _a, _b;
  const hubble = {
    eventName: "ad_products_displayed",
    eventData: {
      widgetName: "AdSelection",
      products: mapProductsForDisplayEvent(meta.products)
    }
  };
  const products = [
    ...(meta == null ? void 0 : meta.products) ?? [],
    ...((_a = meta == null ? void 0 : meta.products) == null ? void 0 : _a.flatMap(({ upgradePaths }) => upgradePaths)) ?? []
  ].filter((product) => typeof product !== "undefined");
  const tracking = {
    eventName: "select_ad_widget_displayed",
    adProductSetId: (_b = meta.context) == null ? void 0 : _b.products.productSetId,
    productPricing: mapProductPricingForDisplayEvent(products)
  };
  return { tracking, hubble };
};
const prepareUpsellsDisplayEvent = (data, meta) => {
  var _a;
  const hubble = {
    eventName: "upsell_products_displayed",
    eventData: {
      widgetName: "AdSelection",
      selectedProductId: meta.selectedParentProductId,
      products: mapProductsForDisplayEvent([
        ...data.upgradePaths,
        ...data.addOns
      ])
    }
  };
  const tracking = {
    eventName: "select_ad_widget_upsell_displayed",
    adProductSetId: (_a = meta.context) == null ? void 0 : _a.products.productSetId,
    productPricing: mapProductPricingForDisplayEvent([
      ...data.upgradePaths,
      ...data.addOns
    ])
  };
  return { tracking, hubble };
};
const prepareSelectionEvent = (data, meta) => {
  var _a, _b, _c, _d;
  const productId = (_a = meta.product) == null ? void 0 : _a.id;
  const adProductsExpectedApps = ((_c = (_b = meta.product) == null ? void 0 : _b.expectations) == null ? void 0 : _c.unit) === "applications" ? meta.product.expectations.count : null;
  const hubble = {
    eventName: "ad_product_selected",
    eventData: {
      widgetName: "AdSelection",
      productId
    }
  };
  const tracking = {
    eventName: "ad_type_pressed",
    productEvents: ["add"],
    productSetProductIds: [productId],
    adProductName: [(_d = meta.product) == null ? void 0 : _d.name.toLowerCase()],
    ...adProductsExpectedApps && {
      adProductsExpectedApps
    }
  };
  return { tracking, hubble };
};
const prepareUpsellSelectionEvent = (data, meta) => {
  var _a, _b;
  const productId = (_a = meta.product) == null ? void 0 : _a.id;
  const hubble = {
    eventName: "upsell_product_selected",
    eventData: {
      widgetName: "AdSelection",
      selectedProductId: meta.selectedParentProductId,
      productId
    }
  };
  const tracking = {
    eventName: "select_ad_widget_upsell_selected",
    adProductSetId: (_b = meta.context) == null ? void 0 : _b.products.productSetId,
    selectedProductId: meta.selectedParentProductId,
    productId
  };
  return { tracking, hubble };
};
const prepareTooltipEvent = (data, meta) => {
  var _a, _b, _c;
  const tracking = {
    eventName: "tooltip_pressed",
    // @TODO: Should we use the tooltip name/id as the identifier rather than the content?
    tooltipType: (_a = data == null ? void 0 : data.content) == null ? void 0 : _a.replace(/[.,]/g, "").replace(/[\s_]+/g, "-").toLowerCase(),
    adProductName: [(_b = meta.product) == null ? void 0 : _b.name],
    productSetProductIds: [(_c = meta.product) == null ? void 0 : _c.id]
  };
  return { tracking };
};
const prepareTrackingData = (data, meta) => {
  if (data.type === "cta" && data.action === "clicked" && "selectedParentProductId" in data) {
    return prepareUpsellSelectionEvent(data, meta);
  }
  if (data.type === "cta" && data.action === "clicked") {
    return prepareSelectionEvent(data, meta);
  }
  if (data.type === "products" && data.action === "displayed") {
    return prepareDisplayEvent(data, meta);
  }
  if (data.type === "upsells" && data.action === "displayed") {
    return prepareUpsellsDisplayEvent(data, meta);
  }
  if (data.type === "tooltip" && data.action === "opened") {
    return prepareTooltipEvent(data, meta);
  }
};
const ProductSelection = ({
  layoutDisplay,
  products,
  selectedProductIds,
  loadingProductIds,
  onProductSelect = () => {
  },
  context,
  onTrackingEvent,
  layout
}) => {
  const layoutDisplayRules = layoutDisplay ? mapLayoutDisplay(layoutDisplay) : void 0;
  return /* @__PURE__ */ jsx(ProductSelectionContext.Provider, { value: context, children: /* @__PURE__ */ jsx(
    TrackingContext.Provider,
    {
      value: {
        trackEvent: (data, inputMeta) => {
          const meta = { ...inputMeta, context };
          const output = prepareTrackingData(data, meta);
          if (output && onTrackingEvent) {
            onTrackingEvent(output);
          }
        }
      },
      children: !(layoutDisplayRules == null ? void 0 : layoutDisplayRules.format) || layoutDisplayRules.format === "grid" ? /* @__PURE__ */ jsx(
        GridLayout,
        {
          layoutDisplayRules,
          products: mapProducts(products, layoutDisplayRules),
          selectedProductIds,
          loadingProductIds,
          onProductSelect: (productId, productType, intent) => {
            const isUrgentLiteBeingRemoved = (selectedProductIds == null ? void 0 : selectedProductIds.includes(PRODUCT_ID.URGENT_LITE)) && productId === PRODUCT_ID.URGENT_LITE;
            if (isUrgentLiteBeingRemoved) {
              onProductSelect(
                PRODUCT_ID.LITE_PRODUCT_ID,
                "AdProduct",
                "update"
              );
              return;
            }
            onProductSelect(productId, productType, intent);
          },
          align: layout == null ? void 0 : layout.align,
          prefixContent: layout == null ? void 0 : layout.prefixContent
        }
      ) : null
    }
  ) });
};
class GetProductSetError extends Error {
  constructor(message) {
    super(`Failed to get product set with error: ${message}`);
    __publicField(this, "errorCode", "PRODUCT_SET_ERROR");
    this.name = "ProductSetError";
  }
}
const useProductSet = ({
  advertiserId,
  productSetId,
  draftId
}) => {
  const { language } = useLanguage();
  const skip = isNaN(Number(advertiserId)) || !productSetId;
  const {
    loading,
    error,
    data: productSet
  } = useGetAdProductsForAdPostingQuery({
    variables: {
      input: { advertiserId, productSetId, locale: language },
      draftId
    },
    skip
  });
  if (skip) {
    return { loading: true, error, adProducts: void 0, display: void 0 };
  }
  if (productSet) {
    if (productSet.adProductsForAdPosting.__typename === "ProductSetExpiredError" || productSet.adProductsForAdPosting.__typename === "ProductSetNotFoundError") {
      const retryableError = new GetProductSetError(
        productSet.adProductsForAdPosting.message
      );
      return {
        loading,
        error: retryableError,
        adProducts: void 0,
        display: void 0
      };
    }
    if (productSet.adProductsForAdPosting.__typename !== "ProductList") {
      const productSetError = new Error(
        `adProductsForAdPosting must be a ProductList. Received ${productSet == null ? void 0 : productSet.adProductsForAdPosting.__typename} instead.`
      );
      return {
        loading,
        error: productSetError,
        adProducts: void 0,
        display: void 0
      };
    }
    const display = productSet.adProductsForAdPosting.display;
    const adProducts = productSet.adProductsForAdPosting.products.filter(
      (product) => product && product.__typename === "AdProduct"
    );
    return { display, adProducts, loading, error };
  }
  return { loading, error, adProducts: void 0, display: void 0 };
};
const ProductSelectionConnected = ({
  errorComponent,
  onError,
  onAdProductSelected,
  selectedProductIds,
  context,
  onTrackingEvent,
  layout
}) => {
  var _a;
  const { loading, error, adProducts, display } = useProductSet({
    advertiserId: context.hirer.id,
    productSetId: context.products.productSetId,
    draftId: (_a = context.draft) == null ? void 0 : _a.id
  });
  if (loading && !adProducts) {
    return /* @__PURE__ */ jsx(ContentBlock, { width: "medium", children: /* @__PURE__ */ jsx(Loader, { delayVisibility: true }) });
  }
  if (error || !adProducts || adProducts.length === 0) {
    onError(error ?? new Error("Did not receive any ad products"));
    return errorComponent;
  }
  return /* @__PURE__ */ jsx(
    ProductSelection,
    {
      layoutDisplay: display,
      products: adProducts,
      selectedProductIds,
      onProductSelect: onAdProductSelected,
      context,
      onTrackingEvent,
      layout
    }
  );
};
const ProductSelectionConnectedCart = ({
  onAdProductSelected,
  context,
  onTrackingEvent,
  layout,
  shoppingCart,
  currentProduct
}) => {
  var _a;
  const shoppingCartContext = useContext(ShoppingCartContext);
  const {
    getAdProductsDocument,
    subscriptionResponseKey,
    key: subscriptionUniqueIdKey,
    getAdProductsDocumentConstructVariables
  } = shoppingCart;
  const {
    productResponses,
    cartState,
    cartProductInProgress,
    addLineItem,
    removeLineItem,
    subscribeForProducts,
    unsubscribeForProducts
  } = shoppingCartContext;
  const client = useApolloClient();
  const getAdProductsQuery = useCallback(
    async (productSetId) => await client.query({
      query: getAdProductsDocument,
      variables: getAdProductsDocumentConstructVariables(productSetId)
    }),
    [getAdProductsDocument, getAdProductsDocumentConstructVariables, client]
  );
  useEffect(() => {
    subscribeForProducts(
      subscriptionUniqueIdKey,
      subscriptionResponseKey,
      getAdProductsQuery
    );
    return () => {
      if (typeof unsubscribeForProducts !== "undefined")
        unsubscribeForProducts(subscriptionUniqueIdKey);
    };
  }, [
    getAdProductsQuery,
    subscribeForProducts,
    subscriptionUniqueIdKey,
    subscriptionResponseKey,
    unsubscribeForProducts
  ]);
  const productResponse = productResponses[subscriptionUniqueIdKey];
  const onProductSelect = (productId, productType, intent) => {
    switch (intent) {
      case "add":
      case "update":
        addLineItem({ productId, productType });
        break;
      case "remove":
        removeLineItem({ productId, productType });
        break;
    }
    if (onAdProductSelected) {
      onAdProductSelected(productId);
    }
  };
  const selectedProductIds = ((cartState == null ? void 0 : cartState.items) ?? []).map(
    (cartItem) => cartItem.product.id
  );
  const loadingProductIds = Object.keys(cartProductInProgress);
  const products = (_a = productResponse == null ? void 0 : productResponse.products) == null ? void 0 : _a.products;
  const layoutDisplay = productResponse == null ? void 0 : productResponse.display;
  if ((productResponse == null ? void 0 : productResponse.loading) && !(products == null ? void 0 : products.length)) {
    return /* @__PURE__ */ jsx(ContentBlock, { width: "medium", children: /* @__PURE__ */ jsx(Loader, { delayVisibility: true }) });
  }
  if (productResponse == null ? void 0 : productResponse.error) {
    throw productResponse == null ? void 0 : productResponse.error;
  }
  if (products && !products.length) {
    throw new Error("PRODUCT_SELECTION_NO_PRODUCTS_ERROR");
  }
  const productsWithCurrentProduct = currentProduct && products ? [currentProduct, ...products] : products;
  return /* @__PURE__ */ jsx(
    ProductSelection,
    {
      products: productsWithCurrentProduct ?? [],
      layoutDisplay,
      selectedProductIds,
      loadingProductIds,
      onProductSelect,
      context,
      onTrackingEvent,
      layout
    }
  );
};
export {
  ProductSelection as AdProductSelection,
  ProductSelectionConnected as AdProductSelectionConnected,
  ProductSelectionConnectedCart as AdProductSelectionConnectedCart
};
